/**
 * 字段特殊处理Hook
 * 职责: 处理适应症阶段、机构角色、里程碑等特殊字段的联动逻辑
 */

import type { IFieldGroup } from '@pharmsnap/pharmsnap-web/composable/useDrugFinderExpertSearch'
import type { IBAcTag } from '@pharmsnap/shared/types'
import { generateHardData } from '@pharmsnap/shared/utils'
import { ALL_TAG_ID, FIELD_SPECIAL_HANDLERS, getGroupField, setGroupField } from '../config/fieldConfig'

export function useFieldSpecialHandlers() {
  /**
   * 适应症阶段字段变更处理
   * 根据选择的阶段更新适应症字段的fields
   */
  const changeIndicationPhaseItems = (group: IFieldGroup, val: IBAcTag): void => {
    if (!group.fields.indicationItems) return

    const phaseId = val.id
    const fields = FIELD_SPECIAL_HANDLERS.indicationPhase[phaseId as keyof typeof FIELD_SPECIAL_HANDLERS.indicationPhase]

    if (fields) {
      group.fields.indicationItems.fields = fields
    }
  }

  /**
   * 组织角色字段变更处理
   * 根据选择的角色更新组织字段的fields
   */
  const changeOrgRoleItems = (group: IFieldGroup, val: IBAcTag): void => {
    if (!group.fields.organizationItems) return

    const selectedValues = getGroupField(group, 'orgRoleItems')?.selectedValues || []
    if (!selectedValues || selectedValues.length === 0) return

    const newFields: string[] = []

    selectedValues.forEach((item) => {
      const roleId = item.id
      const fields = FIELD_SPECIAL_HANDLERS.orgRole[roleId as keyof typeof FIELD_SPECIAL_HANDLERS.orgRole]

      if (fields) {
        newFields.push(...fields)
      }
    })

    // 去重并设置
    setGroupField(group, 'organizationItems', { fields: Array.from(new Set(newFields)) })
  }

  /**
   * 里程碑字段变更处理
   * 当选择All时，清空日期选择
   */
  const changeMilestoneItems = (group: IFieldGroup, val: IBAcTag): void => {
    if (!group.fields.milestoneDate) return

    if (val.id === ALL_TAG_ID) {
      setGroupField(group, 'milestoneDate', { selectedValues: [] })
    }

    const milestoneSelectedValues = getGroupField(group, 'milestoneItems')?.selectedValues || []
    if (milestoneSelectedValues && milestoneSelectedValues.length > 0 && milestoneSelectedValues[0]?.id === ALL_TAG_ID) {
      setGroupField(group, 'milestoneDate', { selectedValues: [] })
    }
  }

  /**
   * 检查日期字段是否可点击
   * 用于milestone和currentRdStatus中的日期字段
   */
  const isDateFieldsClickable = (group: IFieldGroup): boolean => {
    if (group.property === 'milestone') {
      // milestoneItems 只要有非 All 的 selectedValues 即可点击
      const selected = getGroupField(group, 'milestoneItems')?.selectedValues || []
      return Array.isArray(selected) && selected.length > 0 && selected[0]?.id !== ALL_TAG_ID
    }

    if (group.property === 'currentRdStatus') {
      // 任意 field 有非 All 的 selectedValues 或 tags 即可点击
      return Object.values(group.fields).some((field) => {
        const selectedValues = field.selectedValues
        const tags = field.tags
        return (
          (Array.isArray(selectedValues) && selectedValues.length > 0 && selectedValues[0]?.id !== ALL_TAG_ID) ||
          (Array.isArray(tags) && tags.length > 0)
        )
      })
    }

    return false
  }

  /**
   * 检查组织角色字段是否可点击
   */
  const isOrgRoleClickable = (group: IFieldGroup): boolean => {
    if (group.property !== 'organization') return false

    return Object.values(group.fields).some((field) => {
      const selectedValues = field.selectedValues
      const tags = field.tags

      if (selectedValues?.length && selectedValues.length > 0) {
        return selectedValues[0]?.id !== ALL_TAG_ID
      }
      if (tags?.length && tags.length > 0) {
        return tags[0]?.id !== ALL_TAG_ID
      }
      return false
    })
  }

  /**
   * 恢复适应症阶段字段
   * 根据indicationItems的fields推断应该选择的阶段
   */
  const restoreIndicationPhaseItems = (group: IFieldGroup): void => {
    const fields = group.fields.indicationItems?.fields || []
    let phaseId = ALL_TAG_ID

    if (fields.length === 2) {
      phaseId = ALL_TAG_ID
    } else if (fields[0] === 'RESEARCH_DISEASE') {
      phaseId = 'Active'
    } else if (fields[0] === 'NON_RESEARCH_DISEASE') {
      phaseId = 'Inactive'
    }

    setGroupField(group, 'indicationPhaseItems', { selectedValues: generateHardData('indicationPhaseItems').filter((o) => o.id === phaseId) })
  }

  /**
   * 恢复组织角色字段
   * 根据organizationItems的fields推断应该选择的角色
   */
  const restoreOrgRoleItems = (group: IFieldGroup): void => {
    const fields = group.fields.organizationItems?.fields || []
    const orgRoleIds: string[] = []

    fields.forEach((field) => {
      if (field === 'LICENSE_ORG_ID_ONEID') {
        orgRoleIds.push('EquityInstitutions')
      } else if (field === 'ORIGINAL_DEV_ORG_ID_ONEID') {
        orgRoleIds.push('Originator')
      } else if (field === 'DEV_ORG_ID_ONEID') {
        orgRoleIds.push('Active')
      } else if (field === 'NON_DEV_ORG_ID_ONEID') {
        orgRoleIds.push('Inactive')
      }
    })

    setGroupField(group, 'orgRoleItems', {
      selectedValues: generateHardData('orgRoleItems').filter((o) => {
        if (orgRoleIds.length === generateHardData('orgRoleItems').length - 1) {
          return o.id === 'All'
        }
        return orgRoleIds.includes(o.id)
      }),
    })
  }

  return {
    changeIndicationPhaseItems,
    changeOrgRoleItems,
    changeMilestoneItems,
    isDateFieldsClickable,
    isOrgRoleClickable,
    restoreIndicationPhaseItems,
    restoreOrgRoleItems,
  }
}

export type UseFieldSpecialHandlersReturn = ReturnType<typeof useFieldSpecialHandlers>
